<template>
  <SidebarFileAttachments
    v-if="content === RightDrawerContent.FileAttachments"
  />
  <SidebarFilePreview v-if="content === RightDrawerContent.FilePreview" />
  <SidebarAuditLog
    v-if="content === RightDrawerContent.AuditLog && shouldRenderAuditLog"
  />
  <SfSidepaneHeader
    v-if="content === RightDrawerContent.Communication"
    :ledger-label="ledgerLabel"
    :owner-id="ownerId"
    :owner-type="ownerType"
    :can-add-message="canAddMessage"
  >
    <template #before-new-button>
      <template v-if="onWorkflowReview">
        <WorkflowReviewSidepaneHeaderExtra />
      </template>
    </template>
  </SfSidepaneHeader>
  <!-- We use v-show because the component keeps its fetched communication between openings -->
  <!-- Don't mount when talking points sidebar is showing since it has its own communication handling -->
  <!-- In presentation mode the comms drawer is scoped to the current resource's talking points (see the comms store) -->
  <SfSidepaneCommunication
    v-if="
      communicationMounted &&
      !onWorkflowReview &&
      content !== RightDrawerContent.TalkingPoints
    "
    v-show="content === RightDrawerContent.Communication"
    :ledger-label="ledgerLabel"
    :owner-id="ownerId"
    :owner-type="ownerType"
  />
  <WorkflowReviewSidepane
    v-if="onWorkflowReview"
    v-show="content === RightDrawerContent.Communication"
    :ledger-id="ledgerId"
    :ledger-label="ledgerLabel"
  />
  <SidebarInsightsProperties
    v-if="content === RightDrawerContent.InsightsProperties"
    :extra-data="extraData"
  >
  </SidebarInsightsProperties>
  <SidebarTalkingPoints
    v-if="content === RightDrawerContent.TalkingPoints"
    :extra-data="extraData"
    :ledger-label="ledgerLabel"
  />
  <SidebarAiChat
    v-if="aiChatMounted"
    v-show="content === RightDrawerContent.AiChat"
  />
</template>

<script setup lang="ts">
import { RightDrawerContent } from '@app/shared/types/drawer'

import { useParentElement } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { onBeforeUnmount, watchEffect, computed, ref } from 'vue'

import SfSidepaneHeader from './sf-sidepane-header.vue'
import useOpenRemarkModal from '../../composables/use-open-remark-modal'
import { useCommsNewIndicatorBroadcast } from '@app/application/communication/sections/communication/composables/use-comms-new-indicator-broadcast'
import WorkflowReviewSidepaneHeaderExtra from '@app/application/workflows/screens/workflow-review/components/workflow-review-sidepane-header-extra.vue'
import { SilverfinContext } from '@app/shared/types/sf-data'
import SidebarAuditLog from '@app/application/sidebar-audit-log/sidebar-audit-log.vue'
import SidebarFileAttachments from '@app/application/sidebar-file-attachments/sidebar-file-attachments.vue'
import SidebarInsightsProperties from '@app/application/sidebar-insights-properties/sidebar-insights-properties.vue'
import SidebarFilePreview from '@app/application/sidebar-file-preview/sidebar-file-preview.vue'
import SidebarTalkingPoints from '@app/application/client_meetings/components/sidebar-talking-points.vue'
import SidebarAiChat from '@app/application/sidebar-ai-chat/sidebar-ai-chat-async'
import WorkflowReviewSidepane from '@app/application/workflows/screens/workflow-review/components/workflow-review-sidepane.vue'
import SfSidepaneCommunication from '@app/shared/vue/components/layout/sf-sidepane.vue'
import { rightDrawerSelector } from '@app/utils/domHelpers'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import { readPersistedRightContent } from '@app/shared/pinia/drawer-hydration'

import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import { useContext } from '@app/shared/pinia/context'
import { useDrawer } from '@app/shared/pinia/drawer'

defineOptions({
  name: 'SfSidepaneContentSwitcher',
})

interface BaseProps {
  ledgerLabel?: string
  shouldRenderAuditLog?: boolean
  deferCommunicationMount?: boolean
}

interface SfSidepaneProps extends BaseProps {
  contextId?: number
  contextType?: SilverfinContext.Account | SilverfinContext.ReconciliationText
  onWorkflowReview?: false
  ownerId?: number
  ownerType?: string
  extraData?: Record<string, unknown>
  canAddMessage?: boolean
}

interface WorkflowReviewSidepaneProps extends BaseProps {
  ledgerId?: string
  onWorkflowReview?: true
}

type Props = SfSidepaneProps | WorkflowReviewSidepaneProps

const parent = useParentElement()

const props = withDefaults(defineProps<Props>(), {
  contextId: undefined,
  contextType: undefined,
  ownerId: undefined,
  ownerType: undefined,
  extraData: undefined,
  canAddMessage: true,
  ledgerId: undefined,
  deferCommunicationMount: false,
})

const auditsStore = useAuditsStore()

const contextStore = useContext()

const drawerStore = useDrawer()

const { open, isRightDrawerOpen, rightContent } = storeToRefs(drawerStore)

const presentationMode = window.Silverfin.presentationMode

useCommsNewIndicatorBroadcast(props.onWorkflowReview || !presentationMode)
const usingNewLayout = isUsingNewLayout()

const content = computed(() =>
  usingNewLayout ? rightContent.value : open.value.content
)

const aiChatMounted = ref(false)
watchEffect(() => {
  if (content.value === RightDrawerContent.AiChat) aiChatMounted.value = true
})

const communicationMounted = ref(!props.deferCommunicationMount)
watchEffect(() => {
  if (content.value === RightDrawerContent.Communication)
    communicationMounted.value = true
})

if (
  !props.onWorkflowReview &&
  props.contextId &&
  (props.contextType === SilverfinContext.Account ||
    props.contextType === SilverfinContext.ReconciliationText)
) {
  // Pages except for Workflow Review will be able to discern the context readily on mount
  contextStore.setContext(props.contextType, props.contextId)

  // Eagerly load the changes since last visit to display the appropriate unread count UI
  // Pulse users don't have access to audit endpoints, skip to avoid 403/404s
  if (!window.Silverfin?.isCurrentUserPulse?.()) {
    auditsStore.fetchChangesSinceLastVisit({
      contextId: props.contextId,
      contextType: props.contextType,
    })
  }
}

/**
 * Handle situations where the right sidepanel is opened but no triggers exist to set the drawer, e.g. Document Viewer
 */
watchEffect(() => {
  window.requestAnimationFrame(() => {
    if (
      !parent.value ||
      isRightDrawerOpen.value ||
      (!usingNewLayout && open.value.direction !== 'initial')
    ) {
      return
    }

    const drawer = parent.value.closest(rightDrawerSelector)

    const isDomRightDrawerExpanded =
      drawer &&
      (usingNewLayout
        ? !!document.querySelector(
            "#main-wrapper[data-right-drawer='expanded']"
          )
        : drawer.classList.contains('sf-sidebar__expanded--right'))

    if (isDomRightDrawerExpanded) {
      const target = usingNewLayout
        ? readPersistedRightContent()
        : RightDrawerContent.Communication
      if (target) {
        drawerStore.openRightDrawer(target, true)
      }
    }
  })
})

const openModal = useOpenRemarkModal({
  ledgerId: props.onWorkflowReview ? props.ledgerId : undefined,
  ledgerLabel: props.ledgerLabel,
  ownerId: props.onWorkflowReview ? undefined : props.ownerId,
  ownerType: props.onWorkflowReview ? undefined : props.ownerType,
})

window.eventHub.$on('openNewRemarkModal', openModal)

onBeforeUnmount(() => {
  window.eventHub.$off('openNewRemarkModal', openModal)
})
</script>
